/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form } from 'reactstrap';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import { UserWithEmployee } from './UserSelectionModal';
import OneOnOneService from '../../../../services/PerformanceOneOnOne';
import PriorDiscussionsTable from './PriorDiscussionsTable';
import OneOnOneSummary from './OneOnOne/OneOnOneSummary';
import { useOneOnOneForm } from './OneOnOne/Form/useOneOnOneForm';
import { INITIAL_FORM_DATA } from './OneOnOne/Form/formConstants';
import {
  UserInfoSection,
  PolicySection,
  DescriptionSection,
  CoreValueSection,
  MeetingGoalSection,
  PrivateNotesSection,
} from './OneOnOne/Form/FormSections';

interface OneOnOneFormProps {
  isOpen: boolean;
  toggle: () => void;
  selectedUsers?: UserWithEmployee[];
  onSuccess?: () => void;
  editMode?: boolean;
  oneOnOneId?: string;
}

interface FormData {
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfOneOnOne: string;
  coreValues: string[];
  meetingGoal: string;
  privateNotes: string;
}

const OneOnOneForm = ({
  isOpen,
  toggle,
  selectedUsers = [],
  onSuccess,
  editMode = false,
  oneOnOneId,
}: OneOnOneFormProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);
  const schoolId = currentSchool?.value;
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [submittedForms, setSubmittedForms] = useState<Map<string, FormData>>(new Map());
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = selectedUsers[currentUserIndex];
  const isLastUser = currentUserIndex === selectedUsers.length - 1;
  const hasMultipleUsers = !editMode && selectedUsers.length > 1;

  const { formData, errors, policyViolations, priorDiscussions, handleInputChange, validateForm, setFormData } =
    useOneOnOneForm(isOpen, token, currentUser, submittedForms, editMode, oneOnOneId);

  useEffect(() => {
    if (!isOpen) {
      setCurrentUserIndex(0);
      setSubmittedForms(new Map());
      setShowSummary(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const saveCurrentForm = () => {
    if (currentUser) {
      const newSubmittedForms = new Map(submittedForms);
      newSubmittedForms.set(currentUser.id, { ...formData });
      setSubmittedForms(newSubmittedForms);
    }
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev + 1);
  };

  const handlePrevious = () => {
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent, approved: boolean = true) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(approved);
    } else {
      saveCurrentForm();
      setShowSummary(true);
    }
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(false);
    } else {
      await submitDirectly(false);
    }
  };

  const submitDirectly = async (approved: boolean) => {
    if (!validateForm()) return;
    saveCurrentForm();

    setIsSubmitting(true);
    try {
      const service = OneOnOneService(token);
      const finalForms = new Map(submittedForms);
      if (currentUser) {
        finalForms.set(currentUser.id, { ...formData });
      }

      const entries = Array.from(finalForms.entries());
      const oneOnOnes = entries.map(([userId, formData]: [string, FormData]) => ({
        userId,
        name: formData.name,
        dateOfIncident: formData.dateOfIncident,
        position: formData.position,
        department: formData.department,
        supervisor: formData.supervisor,
        dateOfHire: formData.dateOfHire,
        policyViolationIds: formData.policyViolationIds,
        descriptionOfOneOnOne: formData.descriptionOfOneOnOne,
        coreValues: formData.coreValues,
        meetingGoal: formData.meetingGoal,
        privateNotes: formData.privateNotes,
        approved,
      }));

      await service.create({ schoolId, oneOnOnes });

      toast.success(
        approved
          ? `${
              oneOnOnes.length === 1 ? 'One on One submitted' : `${oneOnOnes.length} One on Ones submitted`
            } successfully!`
          : `${oneOnOnes.length === 1 ? 'Draft saved' : `${oneOnOnes.length} drafts saved`} successfully!`,
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error submitting one on ones:', error);
      toast.error(error?.response?.data?.message || 'Error submitting one on ones. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEditForm = async (approved: boolean = true) => {
    if (!oneOnOneId) return;

    setIsSubmitting(true);
    try {
      const service = OneOnOneService(token);
      const updateData = {
        schoolId,
        dateOfIncident: formData.dateOfIncident,
        policyViolationIds: formData.policyViolationIds,
        descriptionOfOneOnOne: formData.descriptionOfOneOnOne,
        coreValues: formData.coreValues,
        meetingGoal: formData.meetingGoal,
        privateNotes: formData.privateNotes,
        approved,
      };

      await service.update(oneOnOneId, updateData);

      toast.success(approved ? 'One on One updated successfully!' : 'Draft saved successfully!');

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error updating one on one:', error);
      toast.error(error?.response?.data?.message || 'Error updating one on one. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitAllForms = async (approved: boolean = true) => {
    setIsSubmitting(true);
    try {
      const service = OneOnOneService(token);
      const entries = Array.from(submittedForms.entries());
      const oneOnOnes = entries.map(([userId, formData]: [string, FormData]) => ({
        userId,
        name: formData.name,
        dateOfIncident: formData.dateOfIncident,
        position: formData.position,
        department: formData.department,
        supervisor: formData.supervisor,
        dateOfHire: formData.dateOfHire,
        policyViolationIds: formData.policyViolationIds,
        descriptionOfOneOnOne: formData.descriptionOfOneOnOne,
        coreValues: formData.coreValues,
        meetingGoal: formData.meetingGoal,
        privateNotes: formData.privateNotes,
        approved,
      }));

      await service.create({ schoolId, oneOnOnes });

      toast.success(
        approved
          ? `${
              oneOnOnes.length === 1 ? 'One on One submitted' : `${oneOnOnes.length} One on Ones submitted`
            } successfully!`
          : `${oneOnOnes.length === 1 ? 'Draft saved' : `${oneOnOnes.length} drafts saved`} successfully!`,
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error submitting one on ones:', error);
      toast.error(error?.response?.data?.message || 'Error submitting one on ones. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEdit = () => setShowSummary(false);

  const handleCancel = () => {
    setCurrentUserIndex(0);
    setSubmittedForms(new Map());
    setShowSummary(false);
    setFormData(INITIAL_FORM_DATA);
    toggle();
  };

  if (showSummary && !editMode) {
    return (
      <OneOnOneSummary
        isOpen={isOpen}
        toggle={handleCancel}
        submittedForms={submittedForms}
        policyViolations={policyViolations}
        onBackToEdit={handleBackToEdit}
        onSubmit={(approved) => submitAllForms(approved)}
        isSubmitting={isSubmitting}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="xl" style={{ maxWidth: '1200px' }}>
      <ModalHeader toggle={handleCancel}>
        {editMode ? 'Edit One on One' : 'One on One Form'}
        {hasMultipleUsers && (
          <span className="ml-2 text-muted" style={{ fontSize: '0.9rem' }}>
            ({currentUserIndex + 1} of {selectedUsers.length})
          </span>
        )}
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Form onSubmit={handleSubmit}>
          <UserInfoSection formData={formData} errors={errors} onChange={handleInputChange} />

          <PriorDiscussionsTable discussions={priorDiscussions} />

          <PolicySection
            formData={formData}
            errors={errors}
            policyViolations={policyViolations}
            onChange={handleInputChange}
          />

          <DescriptionSection
            value={formData.descriptionOfOneOnOne}
            error={errors.descriptionOfOneOnOne}
            onChange={(value) => handleInputChange('descriptionOfOneOnOne', value)}
          />

          <CoreValueSection
            selectedValues={formData.coreValues}
            error={errors.coreValues}
            onChange={(values) => handleInputChange('coreValues', values)}
          />

          <MeetingGoalSection
            value={formData.meetingGoal}
            error={errors.meetingGoal}
            onChange={(value) => handleInputChange('meetingGoal', value)}
          />

          <PrivateNotesSection
            value={formData.privateNotes}
            onChange={(value) => handleInputChange('privateNotes', value)}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <div className="ml-auto">
          {!editMode && hasMultipleUsers && currentUserIndex > 0 && (
            <Button color="outline-secondary" onClick={handlePrevious} className="mr-2">
              <i className="fas fa-arrow-left mr-2" />
              Previous
            </Button>
          )}
          {!editMode && hasMultipleUsers && !isLastUser ? (
            <Button color="primary" onClick={handleNext}>
              <i className="fas fa-arrow-right mr-2" />
              Next
            </Button>
          ) : (
            <>
              <Button color="warning" onClick={handleSaveDraft} disabled={isSubmitting} className="mr-2">
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2" />
                    Save Draft
                  </>
                )}
              </Button>
              <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2" />
                    {hasMultipleUsers ? 'Review & Submit' : 'Submit'}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default OneOnOneForm;
